import { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Loader2, CheckCircle2, XCircle, Clock,
  CalendarDays, Phone, ArrowRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MAX_POLLS = 10;
const POLL_INTERVAL = 2500;

export default function BookingConfirmation() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [booking, setBooking] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | confirmed | expired | error | processing
  const pollCount = useRef(0);
  const timerRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const poll = async () => {
      try {
        const res = await fetch(`${API_URL}/api/bookings/by-session/${sessionId}`);
        if (!res.ok) throw new Error('not found');
        const data = await res.json();
        setBooking(data);

        if (data.status === 'confirmed') {
          setStatus('confirmed');
          return;
        }
        if (data.status === 'expired') {
          setStatus('expired');
          return;
        }

        // Still pending — keep polling
        pollCount.current += 1;
        if (pollCount.current >= MAX_POLLS) {
          setStatus('processing');
          return;
        }
        timerRef.current = setTimeout(poll, POLL_INTERVAL);
      } catch {
        setStatus('error');
      }
    };

    poll();
    return () => clearTimeout(timerRef.current);
  }, [sessionId]);

  const formatDate = (iso) => {
    if (!iso) return '';
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(y, m - 1, d).toLocaleDateString('en-GB', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
  };

  // Loading state
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 text-[#e2e8f0] animate-spin mx-auto" />
          <p className="text-gray-300 text-lg">Confirming your payment...</p>
          <p className="text-gray-500 text-sm">Please don&apos;t close this page</p>
        </div>
      </div>
    );
  }

  // Error state
  if (status === 'error') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4">
        <Card className="bg-[#1e293b] border-slate-700 p-8 max-w-md w-full text-center" data-testid="booking-error">
          <XCircle className="w-14 h-14 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white heading-font mb-2">Something Went Wrong</h2>
          <p className="text-gray-400 text-sm mb-6">
            We couldn&apos;t find your booking. If you completed payment, please contact us with your confirmation email.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/packages">
              <Button className="bg-[#e2e8f0] text-slate-900 hover:bg-[#cbd5e1]" data-testid="back-packages-btn">
                Back to Packages
              </Button>
            </Link>
            <Link to="/contact">
              <Button variant="outline" className="border-slate-600 text-gray-300 hover:bg-slate-800" data-testid="contact-us-btn">
                Contact Us
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  // Expired state
  if (status === 'expired') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4">
        <Card className="bg-[#1e293b] border-slate-700 p-8 max-w-md w-full text-center" data-testid="booking-expired">
          <Clock className="w-14 h-14 text-amber-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white heading-font mb-2">Booking Expired</h2>
          <p className="text-gray-400 text-sm mb-6">
            The payment window has closed and the date has been released. You&apos;re welcome to try again.
          </p>
          <Link to="/packages">
            <Button className="bg-[#e2e8f0] text-slate-900 hover:bg-[#cbd5e1]" data-testid="try-again-btn">
              Browse Packages
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  // Still processing state
  if (status === 'processing') {
    const handleRetry = () => {
      setStatus('loading');
      pollCount.current = 0;
    };
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4">
        <Card className="bg-[#1e293b] border-slate-700 p-8 max-w-md w-full text-center" data-testid="booking-processing">
          <Clock className="w-14 h-14 text-blue-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white heading-font mb-2">Payment Processing</h2>
          <p className="text-gray-400 text-sm mb-6">
            Your payment is still being processed. This can take a moment. Please check your email for confirmation or try refreshing.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={handleRetry} className="bg-[#e2e8f0] text-slate-900 hover:bg-[#cbd5e1]" data-testid="retry-check-btn">
              Check Again
            </Button>
            <Link to="/contact">
              <Button variant="outline" className="border-slate-600 text-gray-300 hover:bg-slate-800" data-testid="contact-support-btn">
                <Phone className="w-4 h-4 mr-2" />
                Contact Support
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  // Confirmed state
  const depositGBP = booking ? (booking.deposit_pence / 100).toFixed(2) : '0.00';

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4 py-20">
      <Card className="bg-[#1e293b] border-slate-700 max-w-lg w-full overflow-hidden" data-testid="booking-confirmed">
        {/* Success banner */}
        <div className="bg-emerald-600 px-6 py-5 text-center">
          <CheckCircle2 className="w-12 h-12 text-white mx-auto mb-2" />
          <h2 className="text-2xl font-bold text-white heading-font">Booking Confirmed</h2>
          <p className="text-emerald-100 text-sm mt-1">Your event date is secured</p>
        </div>

        <div className="p-6 space-y-5">
          {/* Booking ref */}
          <div className="text-center">
            <p className="text-xs text-gray-400 mb-1">Booking Reference</p>
            <Badge className="bg-slate-800 text-[#e2e8f0] text-lg px-4 py-1.5 font-mono tracking-wider" data-testid="booking-ref">
              {booking?.booking_ref}
            </Badge>
          </div>

          {/* Details */}
          <div className="bg-slate-800/60 rounded-lg divide-y divide-slate-700">
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-sm text-gray-400">Package</span>
              <span className="text-sm font-semibold text-white">
                {booking?.package_name}
                {booking?.addon_package_name && <span className="text-gray-400 font-normal"> + {booking.addon_package_name}</span>}
              </span>
            </div>
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-sm text-gray-400 flex items-center gap-1.5">
                <CalendarDays className="w-3.5 h-3.5" /> Dates
              </span>
              <span className="text-sm font-semibold text-white">
                {formatDate(booking?.event_date)}
                {booking?.num_days > 1 && <span className="text-gray-400 font-normal"> ({booking.num_days} days)</span>}
              </span>
            </div>
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-sm text-gray-400">Deposit Paid</span>
              <span className="text-sm font-semibold text-emerald-400">
                &pound;{depositGBP}
                {booking?.discount_percent > 0 && <span className="text-xs text-emerald-300 ml-1">({booking.discount_percent}% off)</span>}
              </span>
            </div>
            <div className="flex items-center justify-between px-4 py-3">
              <span className="text-sm text-gray-400">Name</span>
              <span className="text-sm text-gray-200">{booking?.customer_name}</span>
            </div>
          </div>

          <p className="text-xs text-gray-500 text-center leading-relaxed">
            A confirmation email has been sent to <span className="text-gray-300">{booking?.customer_email}</span>.{' '}
            Our team will be in touch to finalise the details of your event.
          </p>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            <Link to="/" className="flex-1">
              <Button className="w-full bg-[#e2e8f0] text-slate-900 hover:bg-[#cbd5e1] font-semibold" data-testid="back-home-btn">
                Back to Home
              </Button>
            </Link>
            <a href="tel:02080495480" className="flex-1">
              <Button variant="outline" className="w-full border-slate-600 text-gray-300 hover:bg-slate-800" data-testid="call-us-btn">
                <Phone className="w-4 h-4 mr-2" />
                Call Us
              </Button>
            </a>
          </div>
        </div>
      </Card>
    </div>
  );
}
